import { Button, Checkbox, Input } from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';

import { secAdvApi } from '../../api/sec-adv-api';
import type { ISecAdvFacets, ISecAdvFirmQuery, SecAdvFacetField } from '../../api/sec-adv-contracts';

/**
 * A column funnel's values (SR-423): those among the firms the other filters keep, with their
 * counts, most common first — read from `/firms/facets` as the funnel opens and, for a city or a
 * state, as its box is typed in. The values chosen stay on top. Where the values cannot be read the
 * funnel offers `known` — the field's whole vocabulary, uncounted — and a city's or a state's box
 * can still add its text, so the filter works without them.
 */
export function FacetValues({
  label,
  field,
  query,
  chosen,
  known = [],
  labelOf = (value) => value,
  onChange
}: {
  label: string;
  field: SecAdvFacetField;
  query: ISecAdvFirmQuery;
  chosen: string[];
  /** The field's values where it has a vocabulary — offered while the counts are not read. */
  known?: readonly { value: string; label: string }[];
  labelOf?: (value: string) => string;
  onChange: (values: string[]) => void;
}) {
  const typed = field === 'city' || field === 'state';
  const [prefix, setPrefix] = React.useState('');
  // Each read is held with what it answers, `facets` null when it failed: the values of another
  // query are never offered, and a new prefix keeps the last ones while it is read.
  const [read, setRead] = React.useState<Nullable<IFacetRead>>(null);
  const base = JSON.stringify([field, query]);
  const asked = JSON.stringify([base, prefix.trim()]);
  const held = read?.base === base ? read : null;
  const facets = held?.facets ?? null;
  const failed = held?.asked === asked && held.facets === null;

  React.useEffect(() => {
    const controller = new AbortController();
    const timer = setTimeout(() => {
      secAdvApi
        .listFacets(field, query, prefix.trim(), controller.signal)
        .then((answer) => setRead({ base, asked, facets: answer }))
        .catch(() => {
          if (!controller.signal.aborted) setRead({ base, asked, facets: null });
        });
    }, 200);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
    // `asked` spells the query and the prefix.
  }, [asked]);

  const toggle = (value: string, on: boolean) =>
    onChange(on ? [...chosen, value] : chosen.filter((held) => held !== value));
  const offered: { value: string; count?: number }[] = (
    facets?.values ?? (failed ? known.map((option) => ({ value: option.value })) : [])
  ).filter((facet) => !chosen.includes(facet.value));
  const word = prefix.trim();
  const added = field === 'state' ? word.toUpperCase() : word;
  const canAdd =
    typed &&
    word !== '' &&
    !chosen.some((value) => value.toLowerCase() === word.toLowerCase()) &&
    !offered.some((facet) => facet.value.toLowerCase() === word.toLowerCase());

  return (
    <div className="grid gap-2">
      {typed && (
        <Input
          aria-label={`Find ${label.toLowerCase()}`}
          placeholder={field === 'city' ? 'Find a city' : 'Find a state, e.g. NY'}
          value={prefix}
          onChange={(event) => setPrefix(event.target.value)}
          className="h-8"
        />
      )}
      <fieldset aria-label={label} className="grid max-h-72 gap-2 overflow-y-auto">
        {chosen.map((value) => (
          <label key={`chosen ${value}`} className="flex items-center gap-2">
            <Checkbox checked onCheckedChange={() => toggle(value, false)} />
            <span className="flex-1 truncate">{labelOf(value)}</span>
          </label>
        ))}
        {offered.map((facet) => (
          <label key={facet.value} className="flex items-center gap-2">
            <Checkbox checked={false} onCheckedChange={() => toggle(facet.value, true)} />
            <span className="flex-1 truncate">{labelOf(facet.value)}</span>
            {facet.count !== undefined && (
              <span className="text-xs tabular-nums text-muted-foreground">
                {facet.count.toLocaleString('en-US')}
              </span>
            )}
          </label>
        ))}
        {facets === null && !failed && (
          <p className="text-xs text-muted-foreground">Reading the values…</p>
        )}
        {(facets !== null || failed) && offered.length === 0 && chosen.length === 0 && !canAdd && (
          <p className="text-xs text-muted-foreground">No values.</p>
        )}
      </fieldset>
      {canAdd && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => {
            toggle(added, true);
            setPrefix('');
          }}
        >
          Add “{added}”
        </Button>
      )}
    </div>
  );
}

interface IFacetRead {
  /** The field and the query the values were read under. */
  base: string;
  /** `base` and the prefix. */
  asked: string;
  facets: Nullable<ISecAdvFacets>;
}
